import React from 'react'
import { Avatar, Box, Flex, Text } from '@chakra-ui/react'
import LazyLoadImg from '../../image/LazyLoadImg'

interface AvatarCellProps {
  /** Image URL (profile_picture, logo, etc.) */
  image?: string | null
  /** Display name (used for Avatar fallback initials and alt text) */
  name: string
  /** Optional second line (position, email, etc.) — triggers 32px variant */
  subtitle?: string
  /** Show name initials instead of generic avatar icon when no image */
  showInitials?: boolean
}

/**
 * Shared avatar + name cell for all tables.
 * - 1-line (no subtitle): 24px avatar
 * - 2-line (with subtitle): 32px avatar
 */
const AvatarCell: React.FC<AvatarCellProps> = ({ image, name, subtitle, showInitials }) => {
  const size = subtitle ? 32 : 24

  return (
    <Flex alignItems="center" gap="8px" minW={0}>
      {image ? (
        <Box flexShrink={0} boxSize={`${size}px`}>
          <LazyLoadImg
            src={image}
            alt={name}
            width={size}
            height={size}
            borderRadius="50%"
            objectFit="cover"
            style={{ display: 'block', width: `${size}px`, height: `${size}px` }}
          />
        </Box>
      ) : (
        <Avatar
          boxSize={`${size}px`}
          name={showInitials ? name : undefined}
          flexShrink={0}
          sx={showInitials ? { '& > div': { fontSize: size === 24 ? '10px' : '12px' } } : undefined}
        />
      )}
      <Box minW={0}>
        <Text isTruncated textStyle="l-xs">
          {name}
        </Text>
        {subtitle && (
          <Text isTruncated textStyle="b-xs" color="solid.500" mt="2px">
            {subtitle}
          </Text>
        )}
      </Box>
    </Flex>
  )
}

export default AvatarCell
